import { useState } from "react";
import {
  useTodoStats,
  useTodos,
  useUpdateTodo,
  useArchiveTodo,
  useUnarchiveTodo,
  type TodoItem,
  type TodoFilters,
} from "../api/todos";
import { formatRelative } from "../components/formatting";

function TodoStatusBar() {
  const { data, isLoading, error } = useTodoStats();

  if (isLoading) return <div className="todo-status-bar">Loading...</div>;
  if (error || !data) return <div className="todo-status-bar error">Failed to load stats</div>;

  return (
    <div className="todo-status-bar">
      <div className="todo-status-grid">
        <div className="todo-stat">
          <span className="todo-stat-label">Open</span>
          <span className="todo-stat-value">{data.open}</span>
        </div>
        <div className="todo-stat">
          <span className="todo-stat-label">In Progress</span>
          <span className="todo-stat-value">{data.in_progress}</span>
        </div>
        <div className="todo-stat">
          <span className="todo-stat-label">Overdue</span>
          <span className={`todo-stat-value ${data.overdue > 0 ? "overdue" : ""}`}>
            {data.overdue}
          </span>
        </div>
        <div className="todo-stat">
          <span className="todo-stat-label">Done</span>
          <span className="todo-stat-value">{data.done}</span>
        </div>
      </div>
    </div>
  );
}

function TodoFilterBar({
  filters,
  onChange,
}: {
  filters: TodoFilters;
  onChange: (f: TodoFilters) => void;
}) {
  return (
    <div className="todo-filter-bar">
      <label>
        Status
        <select
          value={filters.status || "all"}
          onChange={(e) => onChange({ ...filters, status: e.target.value })}
        >
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
          <option value="dropped">Dropped</option>
        </select>
      </label>
      <label>
        Priority
        <select
          value={filters.priority || "all"}
          onChange={(e) => onChange({ ...filters, priority: e.target.value })}
        >
          <option value="all">All</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </label>
      <label className="todo-archived-toggle">
        <input
          type="checkbox"
          checked={filters.includeArchived || false}
          onChange={(e) => onChange({ ...filters, includeArchived: e.target.checked })}
        />
        Show Archived
      </label>
    </div>
  );
}

function TodoRow({ todo }: { todo: TodoItem }) {
  const updateMutation = useUpdateTodo();
  const archiveMutation = useArchiveTodo();
  const unarchiveMutation = useUnarchiveTodo();

  const isOverdue =
    todo.due_date &&
    new Date(todo.due_date) < new Date() &&
    ["open", "in_progress"].includes(todo.status);

  const isArchived = Boolean(todo.archived_at);

  return (
    <tr className={`todo-row ${isArchived ? "archived" : ""}`}>
      <td>
        <span className={`todo-priority-badge ${todo.priority}`}>{todo.priority}</span>
      </td>
      <td className="todo-description" title={todo.description}>
        {todo.description}
      </td>
      <td>
        <select
          className={`todo-status-select ${todo.status}`}
          value={todo.status}
          onChange={(e) =>
            updateMutation.mutate({ id: todo.id, updates: { status: e.target.value } })
          }
          disabled={isArchived}
        >
          <option value="open">open</option>
          <option value="in_progress">in_progress</option>
          <option value="done">done</option>
          <option value="dropped">dropped</option>
        </select>
      </td>
      <td>
        <select
          className="todo-priority-select"
          value={todo.priority}
          onChange={(e) =>
            updateMutation.mutate({ id: todo.id, updates: { priority: e.target.value } })
          }
          disabled={isArchived}
        >
          <option value="high">high</option>
          <option value="medium">medium</option>
          <option value="low">low</option>
        </select>
      </td>
      <td className={isOverdue ? "todo-due-overdue" : ""}>
        {todo.due_date ? formatRelative(todo.due_date) : "—"}
      </td>
      <td className="todo-tags-cell">
        {todo.tags?.map((tag) => (
          <span key={tag} className="todo-tag">{tag}</span>
        ))}
      </td>
      <td>
        <span className={`todo-author-badge ${todo.author}`}>{todo.author}</span>
      </td>
      <td className="todo-created">{formatRelative(todo.created_at)}</td>
      <td>
        {isArchived ? (
          <button
            className="btn btn-sm todo-btn-unarchive"
            onClick={() => unarchiveMutation.mutate(todo.id)}
            disabled={unarchiveMutation.isPending}
          >
            Unarchive
          </button>
        ) : (
          ["done", "dropped"].includes(todo.status) && (
            <button
              className="btn btn-sm todo-btn-archive"
              onClick={() => archiveMutation.mutate(todo.id)}
              disabled={archiveMutation.isPending}
            >
              Archive
            </button>
          )
        )}
      </td>
    </tr>
  );
}

function TodoTable({ filters }: { filters: TodoFilters }) {
  const { data, isLoading } = useTodos(filters);

  if (isLoading) return <div className="status-line">Loading todos...</div>;
  if (!data || data.items.length === 0) return <div className="status-line">No todos found.</div>;

  return (
    <table className="todo-table">
      <thead>
        <tr>
          <th>Priority</th>
          <th>Description</th>
          <th>Status</th>
          <th>Set Priority</th>
          <th>Due</th>
          <th>Tags</th>
          <th>Author</th>
          <th>Created</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((todo) => (
          <TodoRow key={todo.id} todo={todo} />
        ))}
      </tbody>
    </table>
  );
}

export function TodosView() {
  const [filters, setFilters] = useState<TodoFilters>({
    status: "all",
    priority: "all",
    includeArchived: false,
  });

  return (
    <div className="todo-view">
      <TodoStatusBar />
      <TodoFilterBar filters={filters} onChange={setFilters} />
      <section className="todo-section">
        <TodoTable filters={filters} />
      </section>
    </div>
  );
}
